import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import CourseCard from "@/components/CourseCard";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass } from "@phosphor-icons/react";

export default function Catalog() {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState({}); // courseId -> progress
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    (async () => {
      try {
        const [coursesRes, enrolRes] = await Promise.all([api.get("/courses"), api.get("/enrollments")]);
        setCourses(coursesRes.data);
        const map = {};
        for (const e of enrolRes.data) map[e.course_id] = e.progress;
        setEnrollments(map);
      } catch (e) {
        setError(formatApiError(e, "Could not load courses."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(() => {
    const s = new Set(courses.map((c) => c.category || "General"));
    return ["All", ...Array.from(s)];
  }, [courses]);

  const filtered = useMemo(() => {
    return courses
      .filter((c) => (category === "All" ? true : (c.category || "General") === category))
      .filter((c) => {
        if (!query.trim()) return true;
        const q = query.toLowerCase();
        return (
          c.title.toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q) ||
          (c.category || "").toLowerCase().includes(q)
        );
      });
  }, [courses, query, category]);

  return (
    <AppLayout title="Course Catalog" eyebrow="Browse">
      <div className="border border-[#EAEAE6] bg-white p-5 mb-8 flex flex-col md:flex-row md:items-center gap-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A8A1]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search courses, topics, descriptions..."
            className="pl-10 h-11 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
            data-testid="catalog-search-input"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`catalog-filter-${c}`}
              className={`px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold border whitespace-nowrap transition-colors ${
                category === c
                  ? "bg-[#1C1C19] text-white border-[#1C1C19]"
                  : "bg-white text-[#5C5C58] border-[#D8D8D2] hover:border-[#1C1C19] hover:text-[#1C1C19]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-[#993333] mb-4">{error}</div>}

      {loading ? (
        <div className="text-sm text-[#8A8A85]">Loading catalog...</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#D8D8D2] p-10 text-center" data-testid="catalog-empty">
          <h3 className="font-heading text-xl font-bold mb-2">No courses match your search</h3>
          <p className="text-[#5C5C58]">Try a different keyword or category.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="catalog-grid">
          {filtered.map((c) => (
            <CourseCard key={c.id} course={c} progress={enrollments[c.id]} />
          ))}
        </div>
      )}
    </AppLayout>
  );
}
